import "./AccountSubscriptions.scss"
import {useEffect, useState} from "react";
import moment from "moment";
import {useTranslation} from "react-i18next";
import ConfirmPopup from "../../../components/ConfirmPopup/ConfirmPopup";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import {ICustomerSubscription} from "../../../models/CustomerSubscription";
import {ClientSubscriptionService} from "../../../services/clientSubscription.service";
import {openMessage} from "../../../store/notification/notificationSlice";
import Spinner from "../../../components/Spiner/Spinner";
import {firstLetterToUpperCase} from "../../../helpers/language.helper";


const AccountSubscriptions = () => {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();

    const {profile} = useAppSelector(state => state.profileSlice);
    const {gymSettings} = useAppSelector(state => state.gymSettingsSlice);

    const [subscription, setSubscription] = useState<{ data: ICustomerSubscription[], loader: boolean }>({
        loader: true,
        data: []
    });
    const [showConfirmCancelSubPopup, setShowConfirmCancelSubPopup] = useState<{visible: boolean, selectedSubscription: null | ICustomerSubscription}>({visible: false, selectedSubscription: null});

    useEffect(() => {
        if (profile?.customerId) {
            getSubscriptions(profile.customerId);
        }
    }, [profile]);

    const getSubscriptions = async (id: number) => {
        const response = await ClientSubscriptionService.getCustomerSubscriptions(id);
        if (response.status === 200) {
            response.data.forEach(el => {
                if (moment(el.start).isAfter(moment())) {
                    el.status = "future";
                }
                if (el.status === "active" && el.autoRenewalEnabled) {
                    el.actions = [
                        {
                            id: "cancel",
                            text: t("Cancel")
                        }
                    ]
                }
            })
            setSubscription({data: response.data, loader: false});
        } else {
            setSubscription({data: [], loader: false});
            dispatch(openMessage({type: "error", title: t("SomethingWentWrong")}))
        }
    }

    const hideConfirmCancelSubPopup = () => {
        setShowConfirmCancelSubPopup({visible: false, selectedSubscription: null});
    }

    const cancelSubscription = (subscription: ICustomerSubscription | null) => {
        if (!subscription) return;
        if (!gymSettings || !gymSettings.cancelAutoRenewalForClient) {
            dispatch(openMessage({
                type: "error",
                title: t("SubscriptionAutoRenewalDisabledErrorMessage")
            }))
            hideConfirmCancelSubPopup();
            return;
        }

        finishAutoRenewalSubscription(subscription);

    }

    const finishAutoRenewalSubscription = async (subscription: ICustomerSubscription) => {
        if (!profile) return;
        try {
            const response = await ClientSubscriptionService.finishAutoRenewalSubscription(subscription.id);
            if (response.status === 200) {
                dispatch(openMessage({title: t("AutoRenewalSubscriptionStopped"), type: "success"}));
                getSubscriptions(profile.customerId);
            }
        } catch (e) {
            dispatch(openMessage({title: t("FailedToCancelSubscription"), type: "error"}));
        }
        hideConfirmCancelSubPopup();
    }

    if (subscription.loader) {
        return (
            <div className="loader-wrapper">
                <Spinner/>
            </div>
        )
    }

    if (!subscription.data.length) {
        return (
            <section className="account-subscription-page">
                <div className="empty-box">
                    <h2 className="title">
                        {t("HaveNotSubscriptions")}
                    </h2>
                </div>
            </section>
        )
    }

    return (
        <>
            <section className="account-subscription-page">
                <ul className="list">
                    <li className="list-item list-item__main">
                        <ul className="row-list">
                            <li className="row-list__item">
                                {t("Type")}
                            </li>
                            <li className="row-list__item">
                                {t("Status")}
                            </li>
                            <li className="row-list__item">
                                {t("AutoRenewal")}
                            </li>
                            <li className="row-list__item">
                                {t("SessionsLeft")}
                            </li>
                            <li className="row-list__item">
                                {t("Starts")}
                            </li>
                            <li className="row-list__item">
                                {t("Ends")}
                            </li>
                            <li className="row-list__item">
                                {t("Action")}
                            </li>
                        </ul>
                    </li>
                    {subscription.data.map(subscription => {
                        return (
                            <li key={subscription.id} className="list-item">
                                <ul className="row-list">
                                    <li className="row-list__item">
                                        <div className="content">
                                            <h4 className="title">{t("Type")}</h4>
                                            <p className="info">{subscription.subscription}</p>
                                        </div>
                                    </li>
                                    <li className="row-list__item">
                                        <div className="content">
                                            <h4 className="title">{t("Status")}</h4>
                                            <p className={`info status ${subscription.status}`}>{firstLetterToUpperCase(subscription.status)}</p>
                                        </div>
                                    </li>
                                    <li className="row-list__item">
                                        <div className="content">
                                            <h4 className="title">{t("AutoRenewal")}</h4>
                                            <p className={`info auto-renewal ${subscription.autoRenewalEnabled ? "green" : "red"}`}>{subscription.autoRenewalEnabled ? 'ON' : 'OFF'}</p>
                                        </div>
                                    </li>
                                    <li className="row-list__item">
                                        <div className="content">
                                            <h4 className="title">{t("SessionsLeft")}</h4>
                                            <p className="info">{subscription.sessions > 900000000 ? t("Unlimited") : subscription.sessions}</p>
                                        </div>
                                    </li>
                                    <li className="row-list__item">
                                        <div className="content">
                                            <h4 className="title">{t("Starts")}</h4>
                                            <p className="info">{moment(subscription.start).format("DD/MM/YYYY")}</p>
                                        </div>
                                    </li>
                                    <li className="row-list__item">
                                        <div className="content">
                                            <h4 className="title">{t("Ends")}</h4>
                                            <p className="info">{moment(subscription.end).format("DD/MM/YYYY")}</p>
                                        </div>
                                    </li>
                                    <li className="row-list__item">
                                        <div className="content">
                                            <h4 className="title">{t("Action")}</h4>
                                            <div className="info action" >{subscription.actions && subscription.actions[0] ? (
                                                <p onClick={() => setShowConfirmCancelSubPopup({visible: true, selectedSubscription: subscription})} className={"cancel"}>{subscription.actions[0].text}</p>
                                            ) : (
                                                <p>-</p>
                                            )}</div>
                                        </div>
                                    </li>
                                </ul>
                            </li>
                        )
                    })}
                </ul>
            </section>
            {
                showConfirmCancelSubPopup.visible && !!showConfirmCancelSubPopup.selectedSubscription ? (
                    <ConfirmPopup
                        text={t("AreYouSureYouWantToCancelAutoRenewalForThisSubscription")}
                        clickOnYes={() => cancelSubscription(showConfirmCancelSubPopup.selectedSubscription)}
                        clickOnCancel={hideConfirmCancelSubPopup}
                    />
                ) : null
            }
        </>
    )
}

export default AccountSubscriptions;